import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import { HitlLoadError, HitlPending } from "./HitlStatus";
import type { HitlToolProps } from "./hitlTools";
import { parseToolBody } from "./parseToolBody";

// Mirrors the backend `ask_user_question` schema (analysis-agent
// interactive_ui_tools.py). The "Other" choice is frontend-only, not on the wire.
interface AskOption {
  label: string;
  description: string;
}

interface AskQuestion {
  header: string;
  question: string;
  multiSelect: boolean;
  options: AskOption[];
}

// Tool-result payload shared by the producer (`formatAnswer`) and consumer
// (`AnsweredSummary`) so the shape can't drift.
interface AnsweredQuestion {
  header: string;
  question: string;
  answers: string[];
}

// Sentinel label for the synthesized "Other" choice, kept distinct from any
// real option label.
const OTHER = "__other__";

interface QuestionState {
  // Selected option labels (plus OTHER when the free-text choice is picked).
  selected: string[];
  otherText: string;
}

function parseQuestions(args: string): AskQuestion[] | null {
  const view = parseToolBody(args);
  if (view.kind !== "json") return null;
  const questions = (view.value as { questions?: unknown }).questions;
  if (!Array.isArray(questions) || questions.length === 0) return null;
  return questions as AskQuestion[];
}

// Build the tool-result payload the agent reads back; "Other" free text is
// inlined as `Other: <text>`.
function formatAnswer(questions: AskQuestion[], state: QuestionState[]): string {
  const answers: AnsweredQuestion[] = questions.map((q, i) => {
    const { selected, otherText } = state[i];
    const labels = selected
      .filter((s) => s !== OTHER)
      .concat(selected.includes(OTHER) ? [`Other: ${otherText.trim()}`] : []);
    return { header: q.header, question: q.question, answers: labels };
  });
  return JSON.stringify(answers);
}

// Answered once a real option is picked, or "Other" with non-empty text.
// Gates Submit and drives the per-tab indicators.
const isAnswered = (qs: QuestionState) =>
  qs.selected.some((s) => s !== OTHER) ||
  (qs.selected.includes(OTHER) && qs.otherText.trim().length > 0);

export const AskUserQuestionCard = ({ entry, isRunning, onAnswer }: HitlToolProps) => {
  const questions = useMemo(() => parseQuestions(entry.args), [entry.args]);
  const answered = entry.result !== undefined;

  // Keyed by question index, not a parallel array: questions can be null while
  // args stream in, so a fixed-length array would desync once they arrive.
  const [state, setState] = useState<Record<number, QuestionState>>({});
  const [submitted, setSubmitted] = useState(false);
  // Controlled tab so the answered dots and content stay in sync; only matters
  // with multiple questions.
  const [activeTab, setActiveTab] = useState("0");

  const getQ = (i: number): QuestionState => state[i] ?? { selected: [], otherText: "" };

  // Answered wins first: the summary reads `entry.result`, so it renders even
  // if the original args were unparseable.
  if (answered) {
    return <AnsweredSummary result={entry.result ?? ""} />;
  }

  // Args don't parse. Expected while the call is still streaming — show a
  // placeholder; once the run settles a parse failure is real, so surface it.
  if (!questions) {
    return isRunning ? (
      <HitlPending>Preparing questions…</HitlPending>
    ) : (
      <HitlLoadError>Couldn&apos;t load this question.</HitlLoadError>
    );
  }

  const toggle = (qIdx: number, label: string, multiSelect: boolean) => {
    setState((prev) => {
      const qs = prev[qIdx] ?? { selected: [], otherText: "" };
      if (multiSelect) {
        const has = qs.selected.includes(label);
        const selected = has ? qs.selected.filter((l) => l !== label) : [...qs.selected, label];
        return { ...prev, [qIdx]: { ...qs, selected } };
      }
      return { ...prev, [qIdx]: { ...qs, selected: [label] } };
    });
  };

  const setOtherText = (qIdx: number, text: string) => {
    setState((prev) => {
      const qs = prev[qIdx] ?? { selected: [], otherText: "" };
      return { ...prev, [qIdx]: { ...qs, otherText: text } };
    });
  };

  const answeredCount = questions.filter((_, i) => isAnswered(getQ(i))).length;
  const allAnswered = answeredCount === questions.length;
  const multiple = questions.length > 1;
  // Read-only (shared/admin) views pass no onAnswer: questions still render,
  // but options are inert and Submit becomes an "Awaiting response" note.
  const readOnly = onAnswer === undefined;
  const locked = submitted || readOnly;

  // Tab navigation: Next advances once the current question is answered, Back
  // is hidden on the first tab, and the last tab swaps Next for Submit.
  const activeIdx = Number(activeTab);
  const isLastTab = activeIdx === questions.length - 1;
  const currentAnswered = isAnswered(getQ(activeIdx));
  const goBack = () => setActiveTab(String(activeIdx - 1));
  const goNext = () => setActiveTab(String(activeIdx + 1));

  const handleSubmit = async () => {
    if (!allAnswered || submitted || !onAnswer) return;
    setSubmitted(true);
    try {
      await onAnswer(
        entry.id,
        formatAnswer(
          questions,
          questions.map((_, i) => getQ(i))
        )
      );
    } catch {
      // Resume failed: unlock so the user can retry; the caller already rolled
      // back the optimistic tool message.
      setSubmitted(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5 shadow-xs min-w-64 max-w-full">
      {multiple ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto flex-wrap justify-start">
            {questions.map((q, qIdx) => (
              <TabsTrigger key={qIdx} value={String(qIdx)} className="gap-1.5">
                <AnsweredDot answered={isAnswered(getQ(qIdx))} />
                {q.header}
              </TabsTrigger>
            ))}
          </TabsList>
          {questions.map((q, qIdx) => (
            <TabsContent key={qIdx} value={String(qIdx)} className="mt-1">
              <QuestionBlock
                question={q}
                state={getQ(qIdx)}
                disabled={locked}
                onToggle={(label) => toggle(qIdx, label, q.multiSelect)}
                onOtherText={(text) => setOtherText(qIdx, text)}
              />
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <QuestionBlock
          question={questions[0]}
          state={getQ(0)}
          disabled={locked}
          showHeader
          onToggle={(label) => toggle(0, label, questions[0].multiSelect)}
          onOtherText={(text) => setOtherText(0, text)}
        />
      )}
      <div className="flex items-center justify-between gap-3">
        {multiple && !readOnly && activeIdx > 0 ? (
          <Button variant="ghost" size="sm" onClick={goBack} disabled={locked}>
            Back
          </Button>
        ) : (
          <span />
        )}
        {readOnly ? (
          <span className="text-xs italic text-muted-foreground">Awaiting response</span>
        ) : (
          <div className="flex items-center gap-3">
            {multiple && (
              <span className="text-xs text-muted-foreground">
                {answeredCount}/{questions.length} answered
              </span>
            )}
            {multiple && !isLastTab ? (
              <Button size="sm" onClick={goNext} disabled={!currentAnswered}>
                Next
              </Button>
            ) : (
              <Button size="sm" onClick={handleSubmit} disabled={!allAnswered || submitted}>
                {submitted ? "Submitting…" : "Submit"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface QuestionBlockProps {
  question: AskQuestion;
  state: QuestionState;
  // Inert options and "Other" field; true after submit and in read-only views.
  disabled: boolean;
  // Render the header chip; needed in single-question mode where no tab trigger
  // carries it.
  showHeader?: boolean;
  onToggle: (label: string) => void;
  onOtherText: (text: string) => void;
}

const QuestionBlock = ({
  question,
  state,
  disabled,
  showHeader = false,
  onToggle,
  onOtherText,
}: QuestionBlockProps) => {
  const otherOn = state.selected.includes(OTHER);
  return (
    <div className="flex flex-col gap-2">
      {showHeader && (
        <span className="self-start rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          {question.header}
        </span>
      )}
      <p className="text-sm font-medium text-foreground">{question.question}</p>
      <div className="flex flex-col gap-1.5">
        {question.options.map((opt) => (
          <OptionRow
            key={opt.label}
            label={opt.label}
            description={opt.description}
            selected={state.selected.includes(opt.label)}
            multiSelect={question.multiSelect}
            disabled={disabled}
            onClick={() => onToggle(opt.label)}
          />
        ))}
        <OptionRow
          label="Other"
          description="Provide your own answer"
          selected={otherOn}
          multiSelect={question.multiSelect}
          disabled={disabled}
          onClick={() => onToggle(OTHER)}
        />
        {otherOn && (
          <Input
            autoFocus
            value={state.otherText}
            disabled={disabled}
            placeholder="Type your answer…"
            onChange={(e) => onOtherText(e.target.value)}
            className="mt-1 h-8 text-sm"
          />
        )}
      </div>
    </div>
  );
};

// Per-tab indicator: filled when answered, hollow ring otherwise.
const AnsweredDot = ({ answered }: { answered: boolean }) => (
  <span
    aria-hidden
    className={cn(
      "size-2 shrink-0 rounded-full",
      answered ? "bg-primary" : "border border-muted-foreground/40"
    )}
  />
);

interface OptionRowProps {
  label: string;
  description: string;
  selected: boolean;
  multiSelect: boolean;
  disabled: boolean;
  onClick: () => void;
}

const OptionRow = ({
  label,
  description,
  selected,
  multiSelect,
  disabled,
  onClick,
}: OptionRowProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-pressed={selected}
    className={cn(
      "flex items-start gap-2.5 rounded-lg border px-3 py-2 text-left transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      selected
        ? "border-primary bg-primary/5"
        : "border-border hover:bg-muted disabled:hover:bg-transparent",
      disabled && "cursor-not-allowed opacity-60"
    )}
  >
    <span
      className={cn(
        "mt-0.5 flex size-4 shrink-0 items-center justify-center border",
        multiSelect ? "rounded-[4px]" : "rounded-full",
        selected ? "border-primary bg-primary text-primary-foreground" : "border-input"
      )}
    >
      {selected && <Check className="size-3" aria-hidden />}
    </span>
    <span className="flex min-w-0 flex-col">
      <span className="text-sm font-medium text-foreground">{label}</span>
      {description && <span className="text-xs text-muted-foreground">{description}</span>}
    </span>
  </button>
);

// Read-only recap shown once answered (after submit and on reload from
// history). Parses the payload this card produced; falls back to raw text if
// the shape is off.
const AnsweredSummary = ({ result }: { result: string }) => {
  const parsed = useMemo(() => {
    const view = parseToolBody(result);
    if (view.kind !== "json" || !Array.isArray(view.value)) return null;
    return view.value as AnsweredQuestion[];
  }, [result]);

  if (!parsed) {
    return (
      <div className="rounded-2xl border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        {result}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-2xl border border-border bg-muted/30 p-3">
      {parsed.map((a, i) => (
        <div key={i} className="flex flex-col gap-0.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {a.header}
          </span>
          <span className="text-sm text-foreground">{a.answers.join(", ")}</span>
        </div>
      ))}
    </div>
  );
};
